// ContentAnalyzer.ts
// Real-time content analysis for dynamic freemium locking
// Analyzes current guitar arrangements against LimitationEngine rules

import type { UserTier } from '../auth/types';
import type { GuitarArrangement } from '../presets/presetSchema';
import { LimitationEngine } from './limitations';

/**
 * Content violations found by analyzing current arrangements
 */
export interface ContentViolations {
  guitarCount: boolean;           // Too many guitars
  premiumScales: string[];        // List of premium scales in use
  overlayFeatures: boolean;       // Using overlay system  
  premiumSounds: boolean;         // Using non-acoustic sounds (for future use)
  premiumFeatures: string[];      // List of other premium features in use
}

/**
 * UI locking state based on content analysis
 */
export interface LockingState {
  isLocked: boolean;
  lockedFeatures: string[];       // Specific features that are locked
  violations: ContentViolations;  // What rules are being violated
  canEdit: boolean;              // Whether user can make changes
  message: string;               // Description of why content is locked
}

/**
 * Content analyzer for dynamic freemium enforcement
 */
export class ContentAnalyzer {
  
  /**
   * Analyze current guitar arrangements against all LimitationEngine rules
   * @param guitars Current guitar arrangements
   * @param userTier User's tier level
   * @returns ContentViolations object detailing what rules are violated
   */
  static analyzeGuitars(guitars: GuitarArrangement[], userTier: UserTier): ContentViolations {
    const violations: ContentViolations = {
      guitarCount: false,
      premiumScales: [],
      overlayFeatures: false,
      premiumSounds: false,
      premiumFeatures: []
    };

    // Check guitar count limit
    const guitarCheck = LimitationEngine.checkGuitarLimit(guitars.length, userTier);
    violations.guitarCount = !guitarCheck.allowed;

    // Check each guitar for premium features
    guitars.forEach(guitar => {
      // Check scale access
      if (!LimitationEngine.checkScaleAccess(guitar.root.scale, userTier)) {
        if (!violations.premiumScales.includes(guitar.root.scale)) {
          violations.premiumScales.push(guitar.root.scale);
        }
      }

      // Check overlay usage
      if (guitar.overlay && !LimitationEngine.checkFeatureAccess('overlay', userTier)) {
        violations.overlayFeatures = true;
        violations.premiumFeatures.push('overlay');
      }

      // Scale degrees are now free for all users - no checking needed

      // Note: Sound type checking will be added here when implemented
      // if (guitar.soundType && !LimitationEngine.checkSoundTypeAccess(guitar.soundType, userTier)) {
      //   violations.premiumSounds = true;
      // }
    });

    // Remove duplicate premium features
    violations.premiumFeatures = [...new Set(violations.premiumFeatures)];

    return violations;
  }

  /**
   * Check if there are any violations that should lock the UI
   * @param violations ContentViolations to check
   * @returns boolean indicating if UI should be locked
   */
  static hasAnyViolations(violations: ContentViolations): boolean {
    return violations.guitarCount || 
           violations.premiumScales.length > 0 || 
           violations.overlayFeatures ||
           violations.premiumSounds ||
           violations.premiumFeatures.length > 0;
  }

  /**
   * Determine if UI should be locked based on violations
   * @param violations ContentViolations to analyze
   * @returns boolean indicating if UI should be locked
   */
  static shouldLockUI(violations: ContentViolations): boolean {
    return this.hasAnyViolations(violations);
  }

  /**
   * Generate a complete locking state for UI components
   * @param guitars Current guitar arrangements
   * @param userTier User's tier level
   * @returns LockingState with complete analysis
   */
  static generateLockingState(guitars: GuitarArrangement[], userTier: UserTier): LockingState {
    const violations = this.analyzeGuitars(guitars, userTier);
    const isLocked = this.shouldLockUI(violations);
    
    const lockedFeatures: string[] = [];
    let message = '';

    if (violations.guitarCount) {
      lockedFeatures.push('guitar-arrangements');
    }
    
    if (violations.premiumScales.length > 0) {
      lockedFeatures.push('scales');
    }
    
    if (violations.overlayFeatures) {
      lockedFeatures.push('overlay');
    }
    
    // Scale degrees are now free - no longer added to locked features

    // Generate appropriate message
    if (isLocked) {
      if (violations.guitarCount) {
        message = LimitationEngine.getUpgradeMessage('guitar-arrangements');
      } else if (violations.premiumScales.length > 0) {
        message = `Premium scales in use: ${violations.premiumScales.join(', ')}. ${LimitationEngine.getUpgradeMessage('scales')}`;
      } else if (violations.overlayFeatures) {
        message = LimitationEngine.getUpgradeMessage('overlay');
      } else {
        message = 'This content uses Premium features. Upgrade to continue editing!';
      }
    }

    return {
      isLocked,
      lockedFeatures,
      violations,
      canEdit: !isLocked,
      message
    };
  }

  /**
   * Check if a specific guitar arrangement would violate rules if loaded
   * @param guitar Guitar arrangement to check
   * @param userTier User's tier level
   * @returns boolean indicating if this guitar would cause violations
   */
  static wouldGuitarViolateRules(guitar: GuitarArrangement, userTier: UserTier): boolean {
    const testViolations = this.analyzeGuitars([guitar], userTier);
    return this.hasAnyViolations(testViolations);
  }

  /**
   * Check if loading a set of arrangements would violate guitar count limit
   * @param arrangements Arrangements to check
   * @param userTier User's tier level
   * @returns boolean indicating if guitar count would be exceeded
   */
  static wouldExceedGuitarLimit(arrangements: GuitarArrangement[], userTier: UserTier): boolean {
    const guitarCheck = LimitationEngine.checkGuitarLimit(arrangements.length, userTier);
    return !guitarCheck.allowed;
  }

  /**
   * Get a user-friendly description of content violations
   * @param violations ContentViolations to describe
   * @returns Array of strings describing the violations
   */
  static getViolationDescriptions(violations: ContentViolations): string[] {
    const descriptions: string[] = [];

    if (violations.guitarCount) {
      descriptions.push('Too many guitar arrangements for free tier');
    }

    if (violations.premiumScales.length > 0) {
      descriptions.push(`Premium scales: ${violations.premiumScales.join(', ')}`);
    }

    if (violations.overlayFeatures) {
      descriptions.push('Scale overlay system in use');
    }

    // Scale degrees are now free - no longer considered a violation

    if (violations.premiumSounds) {
      descriptions.push('Premium sound types in use');
    }

    return descriptions;
  }
}
